import { plannedGoalCreateSchema } from './schema';
import { computeSortKey, getSortedKeys } from './sort-key';
import type { PlannedGoal, PlannedGoalCreate } from './types';

export function getSortedPlannedGoals(
  plannedGoals: PlannedGoal[],
): PlannedGoal[] {
  return [...plannedGoals].sort((a, b) =>
    a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
  );
}

interface PlannedGoalCreateParams {
  planId: string;
  newGoalId: string;
  sortKey: string;
}

interface PlannedGoalReorder {
  plannedGoalId: string;
  newSortKey: string;
  fromPosition: number;
  toPosition: number;
  updatedPlannedGoals: PlannedGoal[];
}

export function getPlannedGoalReorder(
  plannedGoals: PlannedGoal[],
  goalId: string,
  toPosition: number,
): PlannedGoalReorder | null {
  const sorted = getSortedPlannedGoals(plannedGoals);
  const fromPosition = sorted.findIndex((pg) => pg.goalId === goalId);
  if (fromPosition === -1) {
    return null;
  }

  const movingGoal = sorted[fromPosition];
  if (!movingGoal) {
    return null;
  }

  const remainingGoals = sorted.filter((pg) => pg.goalId !== goalId);
  const newSortKey = computeSortKey(
    remainingGoals.map((pg) => pg.sortKey),
    toPosition,
  );
  const updatedPlannedGoals = [
    ...remainingGoals,
    { ...movingGoal, sortKey: newSortKey },
  ];

  return {
    plannedGoalId: movingGoal.id,
    newSortKey,
    fromPosition,
    toPosition,
    updatedPlannedGoals,
  };
}

export function preparePlannedGoalCreate(
  params: PlannedGoalCreateParams,
): PlannedGoalCreate {
  const { planId, newGoalId, sortKey } = params;

  const rawNewPlannedGoal: PlannedGoalCreate = {
    id: crypto.randomUUID(),
    planId,
    goalId: newGoalId,
    sortKey,
  };

  return plannedGoalCreateSchema.parse(rawNewPlannedGoal);
}

export function preparePlannedGoalCreates(
  planId: string,
  goalIds: string[],
  existingPlannedGoals: PlannedGoal[],
): PlannedGoalCreate[] {
  const keySoFar = getSortedKeys(existingPlannedGoals);
  return goalIds.map((goalId) => {
    const sortKey = computeSortKey(keySoFar, keySoFar.length);
    keySoFar.push(sortKey);
    return preparePlannedGoalCreate({ planId, newGoalId: goalId, sortKey });
  });
}
